// ---------------------------------------------------------------------------
// Trim Sheets — Wall-floor junction trim strips for visual polish.
// Creates thin geometry along architectural junctions (skirting boards,
// metal thresholds, wooden baseboards) to cover seams between surfaces.
// ---------------------------------------------------------------------------

import * as THREE from 'three';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TrimType = 'concrete' | 'metal' | 'wood';

export interface TrimDefinition {
  /** World-space start of the trim strip. */
  start: THREE.Vector3;
  /** World-space end of the trim strip. */
  end: THREE.Vector3;
  /** Visual material type. */
  type: TrimType;
  /**
   * Height of the trim strip in metres (default 0.08).
   * Measured upward from the strip's y-origin.
   */
  height?: number;
}

// ---------------------------------------------------------------------------
// Material generation
// ---------------------------------------------------------------------------

const trimMaterialCache = new Map<TrimType, THREE.MeshStandardMaterial>();

/**
 * Returns a cached MeshStandardMaterial appropriate for the given trim type.
 * Callers must NOT dispose these materials directly — use `disposeTrimSheets`.
 */
export function generateTrimMaterial(type: TrimType): THREE.MeshStandardMaterial {
  const cached = trimMaterialCache.get(type);
  if (cached) return cached;

  let mat: THREE.MeshStandardMaterial;

  switch (type) {
    case 'concrete':
      mat = new THREE.MeshStandardMaterial({
        color: 0x7a7060,
        roughness: 0.9,
        metalness: 0.0,
      });
      break;

    case 'metal':
      mat = new THREE.MeshStandardMaterial({
        color: 0x606a78,
        roughness: 0.35,
        metalness: 0.75,
        envMapIntensity: 0.8,
      });
      break;

    case 'wood':
      mat = new THREE.MeshStandardMaterial({
        color: 0x8a6840,
        roughness: 0.85,
        metalness: 0.0,
      });
      break;
  }

  trimMaterialCache.set(type, mat);
  return mat;
}

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

const TRIM_DEPTH = 0.04; // Depth away from the wall (Z extent)

/**
 * Create a single trim strip mesh along the line from `start` to `end`.
 *
 * The strip is a thin box:
 *   width  = distance(start, end)
 *   height = `height` parameter (default 0.08 m)
 *   depth  = 0.04 m (projecting slightly from the wall surface)
 *
 * The mesh origin is placed at the midpoint and oriented so its local +X
 * axis points from start to end.
 */
export function createTrimStrip(
  scene: THREE.Scene,
  start: THREE.Vector3,
  end: THREE.Vector3,
  type: TrimType,
  height = 0.08,
): THREE.Mesh {
  const dir = new THREE.Vector3().subVectors(end, start);
  const length = dir.length();

  if (length < 0.001) {
    // Degenerate strip — return empty mesh without adding to scene
    const emptyMesh = new THREE.Mesh(
      new THREE.BufferGeometry(),
      generateTrimMaterial(type),
    );
    return emptyMesh;
  }

  const geo = new THREE.BoxGeometry(length, height, TRIM_DEPTH);
  const mat = generateTrimMaterial(type);
  const mesh = new THREE.Mesh(geo, mat);

  // Position at midpoint
  const mid = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
  mesh.position.copy(mid);

  // Orient local +X from start to end
  const axis = dir.clone().normalize();
  const quaternion = new THREE.Quaternion();
  quaternion.setFromUnitVectors(new THREE.Vector3(1, 0, 0), axis);
  mesh.quaternion.copy(quaternion);

  mesh.castShadow = true;
  mesh.receiveShadow = true;

  scene.add(mesh);
  trimMeshes.push(mesh);

  return mesh;
}

// ---------------------------------------------------------------------------
// Batch API
// ---------------------------------------------------------------------------

/** All trim meshes currently in the scene (for disposal). */
const trimMeshes: THREE.Mesh[] = [];

/**
 * Batch-create trim strips from an array of definitions.
 *
 * @example
 * addTrimsToScene(scene, [
 *   { start: new THREE.Vector3(-5, 0, 0), end: new THREE.Vector3(5, 0, 0), type: 'concrete' },
 *   { start: new THREE.Vector3(5, 0, 0), end: new THREE.Vector3(5, 0, -8), type: 'metal' },
 * ]);
 */
export function addTrimsToScene(
  scene: THREE.Scene,
  trimDefs: TrimDefinition[],
): void {
  for (const def of trimDefs) {
    createTrimStrip(scene, def.start, def.end, def.type, def.height);
  }
}

// ---------------------------------------------------------------------------
// Cleanup
// ---------------------------------------------------------------------------

/**
 * Remove all trim meshes from their scene and dispose geometry/materials.
 * Call on map unload or scene teardown.
 */
/**
 * Ownership contract: trim materials are owned by `trimMaterialCache`, NOT by
 * individual meshes. Meshes share materials from the cache, so per-mesh disposal
 * of materials would cause double-dispose. Only `disposeTrimSheets` disposes them.
 */
export function disposeTrimSheets(): void {
  for (const mesh of trimMeshes) {
    mesh.parent?.remove(mesh);
    mesh.geometry.dispose();
    // Materials are shared via cache — do NOT dispose per-mesh
  }
  trimMeshes.length = 0;

  // Dispose cached materials — safe because cache is the sole owner and is cleared after.
  // Guard: only dispose if cache is non-empty (prevents issues if called twice).
  if (trimMaterialCache.size > 0) {
    trimMaterialCache.forEach(mat => mat.dispose());
    trimMaterialCache.clear();
  }
}
